import React, { useEffect, useRef } from "react";
import tw from "twrnc";
import { Platform, View } from "react-native";
import {
  SafeAreaProvider,
  initialWindowMetrics,
} from "react-native-safe-area-context";

import { Amplify } from "aws-amplify";
// import { Ionicons } from "@expo/vector-icons";
import { useNavigationContainerRef } from "@react-navigation/native";
import AppConfig from "@/AppConfig";
import SupportDemo from "@/components/support/SupportDemo";
import SupportWidget from "@/components/support/SupportWidget";
import AppRouting from "@/components/AppRouting";
import { ProvideAuth } from "@/components/AuthContext";
// import ChatWidget from "@/components/shared/ChatWidget";
import sentry from "@/sentry";
import { useDeviceContext } from "twrnc";
import { setupGlobalErrorHandlers } from "@/utils/errorHandler";

// Import analytics to ensure it initializes on web
import analytics from "@/analytics";
import { fetchAuthSessionWithLoginMismatchRetry } from "@/utils/amplifyAuthRecovery";
import { captureFundraiserIntentFromUrl } from "@/components/donations/fundraiserIntent";
import { captureFuneralHomeIntentFromUrl } from "@/components/funeral/funeralHomeIntent";
import {
  enableTeamsWorkspace,
  setWorkspaceSelf,
  workspaceRequestHeaders,
} from "@/components/teams/workspace";
import { TEAMS_UI_ENABLED } from "@/components/teams/flag";
import { WorkspaceFrame } from "@/components/teams/WorkspaceBanner";

// Teams (hidden beta): working in accounts shared with you. Off unless the
// flag is on (components/teams/flag.js).
if (TEAMS_UI_ENABLED) {
  enableTeamsWorkspace({ clientId: AppConfig.REACT_APP_CLIENT_ID });
}

const config = {
  Auth: {
    authenticationFlowType: "USER_SRP_AUTH",
    Cognito: {
      identityPoolId: AppConfig.REACT_APP_IDENTITY_POOL_ID,
      allowGuestAccess: true,
      region: AppConfig.REACT_APP_REGION,
      userPoolId: AppConfig.REACT_APP_USERPOOL_ID,
      userPoolClientId: AppConfig.REACT_APP_CLIENT_ID,
      // mandatorySignIn: false,
      aws_federation_target: "COGNITO_USER_POOLS",
      federatedTarget: "COGNITO_USER_POOLS",
      authenticationFlowType: "CUSTOM_AUTH",
      signUpVerificationMethod: "link",
      loginWith: {
        oauth: {
          domain: AppConfig.REACT_APP_COGNITO_DOMAIN,
          scope: ["public_profile", "email"],
          redirectSignIn: `eventlive://signIn`, // Linking.makeUrl('signIn'),
          redirectSignOut: `eventlive://signOut`, //Linking.makeUrl('signOut'),
          responseType: "code", //token
        },
      },
    },
  },
  API: {
    GraphQL: {
      endpoint: AppConfig.REACT_APP_GRAPHQL_ENDPOINT,
      region: AppConfig.REACT_APP_REGION,
      authenticationType: "AWS_IAM",
    },
  },
};

Amplify.configure(config, {
  API: {
    GraphQL: {
      // `request` is { query, variables } for queries and mutations (nothing
      // for the realtime socket).
      headers: async (request) => {
        try {
          const session = await fetchAuthSessionWithLoginMismatchRetry();
          const token = session.tokens?.accessToken?.toString();
          if (!token) {
            return {};
          }
          // Teams: the workspace store learns who's signed in from the
          // session, so even a reload's first requests (before AuthContext
          // has the profile) name the remembered account. No-op when off.
          setWorkspaceSelf(
            session.tokens?.idToken?.payload?.["custom:broadcaster"]
          );
          return {
            JWT: token,
            // Teams: x-el-owner while working in someone else's account
            // (components/teams/workspace.js); nothing in your own.
            ...workspaceRequestHeaders(request),
          };
        } catch (error) {
          return {};
        }
      },
    },
  },
});

console.reportErrorsAsExceptions = false;

// Remember a nonprofit's fundraiser intent (eventlive.pro/nonprofit →
// /signin?…&pledge_org_id=…&next=fundraiser) and a funeral home's free trial
// (eventlive.pro/funeral-streaming → /signin?…&funeral_lead=…&redeem=3free)
// before navigation rewrites the URL.
if (Platform.OS === "web" && typeof window !== "undefined") {
  captureFundraiserIntentFromUrl(window.location.search);
  captureFuneralHomeIntentFromUrl(window.location.search);
}

// The website chat: eventlive.pro and evt.live show this page in an iframe (components/support/embed.js).
// On eventlive.pro it shares the broadcaster sign-in, so signed-in visitors get help with their account.
const supportEmbed =
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  window.location.pathname.replace(/\/+$/, "") === "/support-embed";

function App() {
  const screenRef = useRef(null);
  const navigationRef = useNavigationContainerRef();
  useDeviceContext(tw);

  // Initialize global error handlers
  useEffect(() => {
    const cleanupGlobalErrorHandlers = setupGlobalErrorHandlers();

    // Add navigation context to Sentry
    sentry.setContext("navigation", {
      hasNavigationRef: !!navigationRef,
      platform: Platform.OS,
    });

    return cleanupGlobalErrorHandlers;
  }, [navigationRef]);

  if (__DEV__ && process.env.EXPO_PUBLIC_SUPPORT_DEMO === "true")
    return <SupportDemo />;

  if (supportEmbed)
    return (
      <SafeAreaProvider initialMetrics={initialWindowMetrics}>
        <ProvideAuth navigation={navigationRef}>
          <SupportWidget embedded allowAnonymous />
        </ProvideAuth>
      </SafeAreaProvider>
    );

  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <ProvideAuth navigation={navigationRef}>
        <View style={{ flex: 1 }}>
          <View ref={screenRef} collapsable={false} style={{ flex: 1 }}>
            {/* Teams: "You're working in …'s account" over every screen (web). */}
            {TEAMS_UI_ENABLED && Platform.OS === "web" ? (
              <WorkspaceFrame navigationRef={navigationRef}>
                <AppRouting ref={navigationRef} />
              </WorkspaceFrame>
            ) : (
              <AppRouting ref={navigationRef} />
            )}
          </View>
          <SupportWidget screenRef={screenRef} navigationRef={navigationRef} />
        </View>
        {/* <ChatWidget /> */}
      </ProvideAuth>
    </SafeAreaProvider>
  );
}

export default App;
